import React, { useState } from 'react';
import { useFont } from '../../Design/FontContext';
import './FontsSettings.css';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import CustomFontUploader from './CustomFontUploader';
import { getDatabase, ref as dbRef, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';

const FontsSettings = ({ 
    selectedBigFont, 
    selectedSmallFont, 
    onBigFontChange, 
    onSmallFontChange,
    pageSettings,
    onColorChange,
    onBackgroundColorChange,
    isReadOnly = false,
    isSpacePage = false
}) => {
    const fonts = useFont();
    const [showFontUploader, setShowFontUploader] = useState(false);
    const [fontToDelete, setFontToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Combine system fonts with custom fonts
    const allTitleFonts = [
        ...titleFonts,
        ...(fonts.customFonts?.map(f => f.name) || [])
    ];
    
    const allTextFonts = [
        ...textFonts,
        ...(fonts.customFonts?.map(f => f.name) || [])
    ];

    const isCustomFont = (fontName) => {
        return fonts.customFonts?.some(f => f.name === fontName);
    };

    // Refresh fonts when custom fonts change
    const handleFontRefresh = async () => {
        if (fonts.refreshCustomFonts) {
            await fonts.refreshCustomFonts();
        }
    };

    // Handle font upload completion
    const handleFontUploaded = async (fontData) => {
        console.log('✅ Font uploaded in FontsSettings:', fontData.name);
        // Refresh the font context to include new font
        await handleFontRefresh();
        // Close the uploader
        setShowFontUploader(false);
    };

    // Handle custom font deletion
    const handleDeleteCustomFont = async (fontName, event) => {
        event.stopPropagation(); // Prevent font selection
        setFontToDelete(fontName);
        setShowDeleteModal(true);
    };

    // Confirm font deletion
    const confirmDeleteFont = async () => {
        if (!fontToDelete) return;
        
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
            console.error('❌ User not authenticated');
            setShowDeleteModal(false);
            setFontToDelete(null);
            return;
        }
        
        try {
            const db = getDatabase();
            
            // Find the font ID by name
            const customFont = fonts.customFonts?.find(f => f.name === fontToDelete);
            if (!customFont) {
                console.error('❌ Font not found:', fontToDelete);
                setShowDeleteModal(false);
                setFontToDelete(null);
                return;
            }
            
            // Delete from Firebase Database
            const fontRef = dbRef(db, `/users/${currentUser.uid}/customFonts/${customFont.id}`);
            await remove(fontRef);
            
            // Remove CSS from document head
            const styleElements = document.querySelectorAll(`[data-custom-font="${fontToDelete}"]`);
            styleElements.forEach(el => el.remove());
            
            console.log(`✅ Custom font deleted: ${fontToDelete}`);
            
            // Refresh font context
            await handleFontRefresh();
            
            // Close modal
            setShowDeleteModal(false);
            setFontToDelete(null);
            
        } catch (error) {
            console.error('❌ Error deleting custom font:', error);
            alert('Failed to delete font. Please try again.');
            setShowDeleteModal(false);
            setFontToDelete(null);
        }
    };

    // Cancel font deletion
    const cancelDeleteFont = () => {
        setShowDeleteModal(false);
        setFontToDelete(null);
    };

    return (
        <div className="fonts-settings">
            <div className="settings-section-header">
                <div className="settings-section-title-container">
                    <h3 className="settings-section-title">Typography</h3>
                    {isSpacePage && (
                        <p className="typography-sync-hint">
                            Fonts are automatically synced with the space settings
                        </p>
                    )}
                </div>
                {!isReadOnly && (
                    <div className="typography-font-actions">
                        <button 
                            className="typography-refresh-fonts-btn"
                            onClick={handleFontRefresh}
                            title="Refresh custom fonts"
                        >
                            <i className="fas fa-sync-alt"></i>
                        </button>
                        <button 
                            className="typography-upload-fonts-btn"
                            onClick={() => setShowFontUploader(!showFontUploader)}
                            title="Upload custom fonts"
                        >
                            <i className="fas fa-upload"></i>
                            {showFontUploader ? 'Hide Uploader' : 'Upload Fonts'}
                        </button>
                    </div>
                )}
            </div>

            {/* Custom Font Uploader */}
            {showFontUploader && !isReadOnly && (
                <div className="font-uploader-section">
                    <CustomFontUploader onFontUploaded={handleFontUploaded} />
                </div>
            )}
            
            <div className="font-section">
                <h4 className="font-section-title">Title Font</h4>
                <div className="font-grid">
                    {allTitleFonts.map(font => (
                        <div 
                            key={font} 
                            className={`font-option ${selectedBigFont === font ? 'font-selected' : ''} ${isReadOnly ? 'font-readonly' : ''} ${isCustomFont(font) ? 'custom-font' : ''}`} 
                            onClick={() => {
                                if (!isReadOnly) {
                                    console.log(`🎨 Selecting Title Font: "${font}" (Custom: ${isCustomFont(font)})`);
                                    onBigFontChange(font);
                                }
                            }} 
                            style={{ fontFamily: `"${font}", Arial, sans-serif` }}
                        >
                            {font}
                            {isCustomFont(font) && !isReadOnly && (
                                <button 
                                    className="custom-font-delete-btn"
                                    onClick={(e) => handleDeleteCustomFont(font, e)}
                                    title={`Delete custom font "${font}"`}
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            )}
                            {isCustomFont(font) && (
                                <span className="custom-font-badge">custom</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="font-section">
                <h4 className="font-section-title">Text Font</h4>
                <div className="font-grid">
                    {allTextFonts.map(font => (
                        <div 
                            key={font} 
                            className={`font-option ${selectedSmallFont === font ? 'font-selected' : ''} ${isReadOnly ? 'font-readonly' : ''} ${isCustomFont(font) ? 'custom-font' : ''}`} 
                            onClick={() => {
                                if (!isReadOnly) {
                                    console.log(`🎨 Selecting Text Font: "${font}" (Custom: ${isCustomFont(font)})`);
                                    onSmallFontChange(font);
                                }
                            }} 
                            style={{ fontFamily: `"${font}", Arial, sans-serif` }}
                        >
                            {font}
                            {isCustomFont(font) && !isReadOnly && (
                                <button 
                                    className="custom-font-delete-btn"
                                    onClick={(e) => handleDeleteCustomFont(font, e)}
                                    title={`Delete custom font "${font}"`}
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            )}
                            {isCustomFont(font) && (
                                <span className="custom-font-badge">custom</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="font-delete-modal-overlay">
                    <div className="font-delete-modal">
                        <div className="font-delete-modal-header">
                            <i className="fas fa-exclamation-triangle"></i>
                            <h3>Delete Custom Font</h3>
                        </div>
                        <div className="font-delete-modal-content">
                            <p>Are you sure you want to delete the custom font <strong>"{fontToDelete}"</strong>?</p>
                            <p className="font-delete-warning">This action cannot be undone.</p>
                        </div>
                        <div className="font-delete-modal-actions">
                            <button 
                                className="font-delete-cancel-btn"
                                onClick={cancelDeleteFont}
                            >
                                Cancel
                            </button>
                            <button 
                                className="font-delete-confirm-btn"
                                onClick={confirmDeleteFont}
                            >
                                <i className="fas fa-trash"></i>
                                Delete Font
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default FontsSettings;
